import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, Minus, RefreshCw } from 'lucide-react';
import { fetchWebScrape, fetchWebsiteDocumentCache, cancelWebScrape } from '../../services/api/virtualAssistant';
import { WebsiteData, ExcludedItems, LinkType } from "../../types/CourseData";
import WebsiteTreeView from "./WebsiteTreeView";
import { v4 as uuidv4 } from 'uuid';
import isEqual from "lodash.isequal";
import "../../styles/loader.css";

interface WebsitePreviewTabProps {
  selectedWebsite: number;
  websites: WebsiteData[];
  setWebsites: React.Dispatch<React.SetStateAction<WebsiteData[]>>;
  triggerAutoSave: () => void;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
  systemConfig: { Id: string; ConfigValue: string; }[];
  updateExcludedItems: (courseId: number, type: keyof ExcludedItems, itemId: string) => void;
  setCustomModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>>;
}

const WebsitePreviewTab: React.FC<WebsitePreviewTabProps> = ({
  selectedWebsite,
  websites,
  setWebsites,
  triggerAutoSave,
  setPendingSave,
  systemConfig,
  updateExcludedItems,
  setCustomModal
}) => {
  const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({});
  const [disabledMap, setDisabledMap] = useState<Record<string, boolean>>({});
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [globalExpanded, setGlobalExpanded] = useState<boolean | undefined>(undefined);
  const [showDocumentCache, setShowDocumentCache] = useState(false);

  const cancelledCrawls = useRef<Set<number>>(new Set());
  const pollingTimeouts = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  // Initialize document cache visibility
  useEffect(() => {
    const configValue = systemConfig.find(item => item.Id === 'document_cache_enabled_website')?.ConfigValue;
    const defaultDocumentCache = Boolean(configValue); 
    setShowDocumentCache(defaultDocumentCache);
  }, [systemConfig]);

  // Update checked map based on excluded links
  useEffect(() => {
    if (websites.length > 0) {
      setCheckedMap(prev => {
        const newMap = { ...prev };
        let hasChanges = false;

        websites.forEach(website => {
          if (!website.websites_links || !website.excludedLinks) return;

          const normalizedLinks = Array.isArray(website.websites_links)
            ? website.websites_links
            : [website.websites_links];

          const setChildrenUnchecked = (links: LinkType[], parentExcluded: boolean = false) => {
            links.forEach(link => {
              const isDirectlyExcluded = website.excludedLinks.includes(link.page);
              const shouldBeUnchecked = isDirectlyExcluded || parentExcluded;
              
              if (shouldBeUnchecked && (newMap[link.page] ?? true)) {
                newMap[link.page] = false;
                hasChanges = true;
              }

              if (link.links) {
                setChildrenUnchecked(link.links, shouldBeUnchecked);
              }
            });
          };

          normalizedLinks.forEach(linkItem => {
            setChildrenUnchecked([linkItem]);
          });
        });

        return hasChanges ? newMap : prev;
      });
    }    
  }, [websites]);

  const collectUncheckedLinksFromTree = (links: LinkType[], currentCheckedMap: Record<string, boolean>): string[] => {
    const unchecked: string[] = [];
    
    const traverse = (linkItems: LinkType[]) => {
      linkItems.forEach(link => {
        const isParentChecked = currentCheckedMap[link.page] ?? true;
        
        if (!isParentChecked) {
          unchecked.push(link.page);
        } else if (link.links && link.links.length > 0) {
          traverse(link.links);
        }
      });
    };

    traverse(links);
    return unchecked;
  };

  const setCheckedMapWithAutoSave = useCallback((
    newCheckedMap: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => {
    setCheckedMap(prev => {
      const updatedMap = typeof newCheckedMap === 'function' ? newCheckedMap(prev) : newCheckedMap;

      setWebsites(currentWebsites => {
        let hasChanged = false;

        const updatedWebsites = currentWebsites.map(website => {
          if (!website.websites_links) return website;

          const normalizedLinks = Array.isArray(website.websites_links)
            ? website.websites_links
            : [website.websites_links];

          const uncheckedLinksForThisWebsite: string[] = [];
          normalizedLinks.forEach(linkItem => {
            const uncheckedFromThisTree = collectUncheckedLinksFromTree([linkItem], updatedMap);
            uncheckedLinksForThisWebsite.push(...uncheckedFromThisTree);
          });

          if (!isEqual(website.excludedLinks, uncheckedLinksForThisWebsite)) {
            hasChanged = true;
            return {
              ...website,
              excludedLinks: uncheckedLinksForThisWebsite
            };
          }

          return website;
        });

        return hasChanged ? updatedWebsites : currentWebsites;
      });

      setTimeout(() => {
        triggerAutoSave();
        setPendingSave(true);
      }, 0);

      return updatedMap;
    });
  }, [setWebsites, triggerAutoSave, setPendingSave]);

  const handleToggleExpanded = (linkPage: string, expanded: boolean) => {
    setExpandedStates(prev => ({
      ...prev,
      [linkPage]: expanded
    }));
  };

  const handleGlobalToggle = () => {
    const newAllExpanded = !allExpanded;
    setAllExpanded(newAllExpanded);
    setGlobalExpanded(newAllExpanded);
    
    setTimeout(() => {
      setGlobalExpanded(undefined);
    }, 100);
  };

  const clearPolling = (websiteId: number) => {
    const timeoutId = pollingTimeouts.current.get(websiteId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pollingTimeouts.current.delete(websiteId);
    }
  };

  const pollWebScrape = async (websiteId: number, source: string, document_id?: string) => {
    const pollInterval = 20000;
    if (source === 'click') {
      cancelledCrawls.current.delete(websiteId);
    }
    
    const course = websites.find(c => c.id === websiteId);
    const documentId = document_id ? document_id : course?.document_id;
    
    const fetchStatus = async () => {
      try {
        if (cancelledCrawls.current.has(websiteId)) return;
        if (documentId) {
          const result = await fetchWebsiteDocumentCache(documentId);
          if (cancelledCrawls.current.has(websiteId)) return; 
          
          if (result.status === 'failed') {
            setWebsites(prev =>
              prev.map(c =>
                c.id === websiteId
                  ? { 
                      ...c, 
                      connectionState: 'connected',
                      status: 'failed',
                    }
                  : c
              )
            );
          }

          if (result.status === 'completed') {
            setWebsites(prev => prev.map(course => 
              course.id === websiteId ? { ...course, activeTab: 'PREVIEW' } : course
            ));
            
            if (result.data) {
              const parsedData = result.data;
              setWebsites(prev =>
                prev.map(c =>
                  c.id === websiteId
                    ? {
                        ...c,
                        connectionState: 'connected',
                        websites_links: parsedData || [],
                        status: 'connected',
                      }
                    : c
                )
              );
              if (source === 'click') {
                triggerAutoSave();
                setPendingSave(true);
              }
              clearPolling(websiteId);
            }
          }
              
          if (result.status === 'pending' || result.status === 'running') {
            setWebsites(prev => prev.map(course => 
              course.id === websiteId ? { ...course, activeTab: 'PREVIEW' } : course
            ));
            
            setWebsites(prev => prev.map(c =>
              c.id === websiteId
                ? {
                    ...c,
                    connectionState: 'connected',
                    status: 'connecting',
                    websites_links: [],
                    website_id: uuidv4(),
                  }
                : c
            ));
            
            const timeoutId = setTimeout(fetchStatus, pollInterval);
            pollingTimeouts.current.set(websiteId, timeoutId);       
          } 
          
          if (result?.detail) {
            setWebsites(prev =>
              prev.map(c =>
                c.id === websiteId
                  ? {
                      ...c,
                      status: 'failed',
                      error: result.detail,
                    }
                  : c
              )
            );
            clearPolling(websiteId);
            return;
          }
        }
      } catch (error) {
        setWebsites(prev =>
          prev.map(c =>
            c.id === websiteId
              ? { 
                  ...c, 
                  status: 'failed',
                }
              : c
          )
        );
        clearPolling(websiteId);
      }
    };
    
    clearPolling(websiteId);
    fetchStatus();
  };

  const handleCancelCrawl = (websiteId: number) => {
    const warningMessage = 'This action will cancel the crawling process. Are you sure?';
    
    setCustomModal({
      isOpen: true,
      title: "Cancel Confirmation",
      message: warningMessage,
      type: "confirmation",
      onConfirm: async () => {
        setCustomModal(prev => ({ ...prev, isOpen: false }));

        setCustomModal({
          isOpen: true,
          title: "Cancelling...",
          message: "Stopping the crawling process...",
          type: "loading",
        });

        const website = websites.find(w => w.id === websiteId);
        const documentId = website?.document_id;

        if (documentId) {
          try {
            await cancelWebScrape(documentId);
            setCustomModal({
              isOpen: true,
              title: "Cancelled Successfully",
              message: "The crawling process has been cancelled.",
              type: "success",
            });
          } catch (error: any) {
            setCustomModal({
              isOpen: true,
              title: "Failed to Cancel",
              message: `Unable to stop the crawling process: ${error.message || 'Unknown error'}`,
              type: "error",
            });
          }
        } else {
          setCustomModal({
            isOpen: true,
            title: "Cancelled",
            message: "Local crawling process has been stopped.",
            type: "success",
          });
        }

        cancelledCrawls.current.add(websiteId);
        clearPolling(websiteId);

        setWebsites(prev =>
          prev.map(c =>
            c.id === websiteId
              ? {
                  ...c,
                  connectionState: 'idle',
                  status: 'idle'
                }
              : c
          )
        );

        setWebsites(prev => prev.map(course =>
          course.id === websiteId ? { ...course, activeTab: 'CONNECT' } : course
        ));
        setPendingSave(false);
        triggerAutoSave();
      }
    });
  };

  const handleRefresh = () => {
    const warningMessage = 'This action will crawl the website links again, and it might take a few minutes. Do you want to proceed?';
    
    setCustomModal({
      isOpen: true,
      title: "Refresh Preview Confirmation",
      message: warningMessage,
      type: "confirmation",
      onConfirm: async () => {
        setCustomModal(prev => ({ ...prev, isOpen: false }));
        
        const selectedCourseData = websites.find(c => c.id === selectedWebsite);
        if (selectedCourseData) {
          setWebsites(prev => prev.map(course => 
            course.id === selectedCourseData.id 
              ? { ...course, status: 'connecting', websites_links: [] } 
              : course
          ));
        }
      }
    });
  };

  // Auto-start polling for connected websites without data
  const fetchCalledRef = useRef<{ [key: number]: boolean }>({});

  useEffect(() => {
    const website = websites.find(w => w.id === selectedWebsite);
    if (website && website.connectionState === 'connected' &&
        !fetchCalledRef.current[website.id] && !website.websites_links?.length) {
      fetchCalledRef.current[website.id] = true;
      if (website.document_id) {
        pollWebScrape(selectedWebsite, 'loop', website.document_id);
      }
    }
  }, [selectedWebsite, websites]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      pollingTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
      pollingTimeouts.current.clear();
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex border rounded-lg p-4">
        <div className="w-full pl-4">
          {websites
            .filter((website) => website.id === selectedWebsite)
            .map((website, index) => {
              const normalizedLinks = Array.isArray(website.websites_links) ? website.websites_links : [website.websites_links];
              const isLoading = (!website.websites_links || normalizedLinks.length === 0) && (website.status === 'connecting');

              return (
                <div key={index} className="bg-white shadow-lg p-5 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {website.website_name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {showDocumentCache && website?.status === 'connected' && (
                        <button
                          onClick={handleRefresh}
                          className="flex items-center gap-2 px-4 py-2 bg-black border border-gray-500 rounded-lg text-white font-semibold hover:bg-gray-800 hover:text-gray-200"
                          title="Refresh"
                        >
                          Refresh Preview
                          <RefreshCw size={20} />
                        </button>
                      )}
                      <button
                        onClick={handleGlobalToggle}
                        className="text-gray-700 hover:text-black p-1"
                        title={allExpanded ? "Collapse All" : "Expand All"}
                      >
                        {allExpanded ? (
                          <Minus className="h-5 w-5" />
                        ) : (
                          <Plus className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {website.status === 'failed' ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-4 shadow-sm">
                      <div className="font-semibold text-lg mb-2">Crawling Failed</div>
                      <p className="text-sm mb-4">
                        There was an error while crawling the website. Please check the URL and try again.
                      </p>
                      <button
                        onClick={() => pollWebScrape(selectedWebsite, 'click')}
                        className="mt-2 inline-block bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded transition duration-200"
                      >
                        Click here to try again
                      </button>
                    </div>
                  ) : isLoading ? (
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="dotted-circle">
                        {[...Array(10)].map((_, i) => (
                          <span key={i} className="dot" style={{ "--i": i } as React.CSSProperties}></span>
                        ))}
                      </div>
                      <div className="text-center space-y-4">
                        <p className="text-gray-700 font-medium">
                          Please wait, the website is being crawled.
                        </p>
                        
                        <div className="flex items-center space-x-4">
                          <button
                            className="flex items-center mr-2.5 gap-2 px-4 py-2 bg-black border border-gray-500 rounded-lg text-white font-semibold hover:bg-gray-800 hover:text-gray-200"
                            onClick={() => pollWebScrape(selectedWebsite, 'click')}
                          >
                            Refresh Status
                          </button>

                          <button
                            className="flex items-center mr-2.5 gap-2 px-4 py-2 bg-white border border-gray-500 rounded-lg text-black font-semibold hover:bg-black hover:text-white"
                            onClick={() => handleCancelCrawl(selectedWebsite)}
                          >
                            Cancel Crawling
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {typeof website.websites_links === 'object' && website.websites_links !== null && (
                        <div>
                          {normalizedLinks.map((linkItem, index) => (
                            <WebsiteTreeView
                              key={index}
                              link={linkItem}
                              level={0}
                              isLast={index === normalizedLinks.length - 1}
                              baseurl={website.url}
                              parentExpanded={true}
                              checkedMap={checkedMap}
                              setCheckedMap={setCheckedMapWithAutoSave}
                              disabledMap={disabledMap}
                              setDisabledMap={setDisabledMap}
                              globalExpanded={globalExpanded}
                              onToggleExpanded={handleToggleExpanded}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default WebsitePreviewTab;